import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Grid,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { useQuery } from "@tanstack/react-query";
import HttpService from "../../services/HttpService";
import SupportChatConversationDialog from "./SupportChatConversationDialog";
import CardStatsElement from "../../components/CardStatsElement";

async function fetchConversations() {
  const res = await HttpService.getAxiosClient().get(
    window.API_URL + "/chat/support/conversations"
  );
  return res.data;
}

function statusChip(status) {
  const color = status === "ESCALATED" ? "warning" : "success";
  const label =
    status === "BOT" ? "Assistant" : status === "HUMAN" ? "Agent" : status;
  return <Chip label={label} color={color} size="small" />;
}

export default function SupportChatDashboard() {
  const [selectedConversationId, setSelectedConversationId] = useState(null);

  const {
    data: conversations,
    isFetching,
    isError,
    refetch,
  } = useQuery(["supportConversations"], fetchConversations, {
    refetchOnWindowFocus: false,
  });

  return (
    <Box p={3}>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <SupportAgentIcon sx={{ color: "#0d7f81", fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Support Chat Dashboard
          </Typography>
          {isFetching && <CircularProgress size={18} sx={{ ml: 1 }} />}
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load conversations.
        </Alert>
      )}

      {/* Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <CardStatsElement
          isLoadingStats={isFetching}
          isErrorStats={isError}
          text="Total Active"
          data={(conversations || []).length}
          md={3}
          countFontSize={45}
          headerTypograhy="body1"
        />
        <CardStatsElement
          isLoadingStats={isFetching}
          isErrorStats={isError}
          text="Waiting for Agent"
          data={
            (conversations || []).filter((c) => c.status === "ESCALATED").length
          }
          md={3}
          countFontSize={45}
          headerTypograhy="body1"
        />
        <CardStatsElement
          isLoadingStats={isFetching}
          isErrorStats={isError}
          text="With Agent"
          data={
            (conversations || []).filter((c) => c.status === "HUMAN").length
          }
          md={3}
          countFontSize={45}
          headerTypograhy="body1"
        />
        <CardStatsElement
          isLoadingStats={isFetching}
          isErrorStats={isError}
          text="Unassigned"
          data={(conversations || []).filter((c) => !c.assignedAgentId).length}
          md={3}
          countFontSize={45}
          headerTypograhy="body1"
        />
      </Grid>

      <Paper elevation={2}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#0d7f81" }}>
              {[
                "Conversation ID",
                "Status",
                "Member / Guest",
                "Assigned Agent",
                "Last Updated",
              ].map((h) => (
                <TableCell
                  key={h}
                  sx={{ color: "white", fontWeight: "bold", fontSize: 13 }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!conversations || conversations.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  {isFetching
                    ? "Loading…"
                    : "No escalated conversations at this time."}
                </TableCell>
              </TableRow>
            ) : (
              conversations.map((conv) => (
                <TableRow
                  key={conv.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelectedConversationId(conv.id)}
                >
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {conv.id}
                  </TableCell>
                  <TableCell>{statusChip(conv.status)}</TableCell>
                  <TableCell>
                    {conv.customerFirstName ? (
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {conv.customerFirstName} {conv.customerLastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {conv.customerEmail}
                        </Typography>
                      </Box>
                    ) : (
                      conv.guestRef || conv.ownerId || "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {conv.assignedAgentId || (
                      <em style={{ color: "#999" }}>Unassigned</em>
                    )}
                  </TableCell>
                  <TableCell>
                    {conv.updatedAt
                      ? new Date(conv.updatedAt).toLocaleString()
                      : "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
      <SupportChatConversationDialog
        conversationId={selectedConversationId}
        onClose={() => setSelectedConversationId(null)}
      />
    </Box>
  );
}
